<script setup lang="ts">
import { onErrorCaptured, ref } from 'vue'
import * as Sentry from '@sentry/vue'
import { useI18n } from '@/composables/useI18n'

const { t } = useI18n()
const hasError = ref(false)
// Raw technical message — shown ONLY in dev. Exposing exception/stack text to
// users in built bundles (preprod/prod) leaks internals and looks broken; they
// get a safe Sentry reference id instead, and the detail goes to Sentry.
const errorMessage = ref('')
const eventId = ref('')
const isDev = import.meta.env.DEV

// Capture errors and send to Sentry
onErrorCaptured((err) => {
  hasError.value = true

  // Extract error message (dev-only display)
  if (err instanceof Error) {
    errorMessage.value = err.message
  } else if (typeof err === 'string') {
    errorMessage.value = err
  } else {
    errorMessage.value = 'An unexpected error occurred'
  }

  // Capture in Sentry; keep the event id so users can quote it to support.
  eventId.value = Sentry.captureException(err)

  // Return false to prevent propagation to parent
  return false
})
</script>

<template>
  <div v-if="hasError" class="error-boundary-fallback">
    <div class="error-container">
      <h1>{{ t('error.somethingWrong') }}</h1>
      <p>{{ t('error.notified') }}</p>
      <!-- Dev only: raw technical detail to debug locally. -->
      <p v-if="isDev && errorMessage" class="error-details">{{ errorMessage }}</p>
      <!-- Built (preprod/prod): safe reference id, never the exception text. -->
      <p v-else-if="eventId" class="error-reference">{{ t('error.reference') }} {{ eventId }}</p>
      <button @click="hasError = false" class="retry-button">{{ t('common.tryAgain') }}</button>
    </div>
  </div>
  <slot v-else />
</template>

<style scoped>
.error-boundary-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: var(--app-background);
  padding: 2rem;
}

.error-container {
  background-color: var(--app-surface-card);
  border: 1px solid var(--app-border);
  border-radius: var(--app-card-border-radius);
  padding: 3rem;
  max-width: 500px;
  text-align: center;
  box-shadow: var(--app-shadow-sm);
}

.error-container h1 {
  margin: 0 0 1rem 0;
  color: var(--app-text-primary);
  font-size: 1.5rem;
}

.error-container p {
  margin: 0.5rem 0;
  color: var(--app-text-secondary);
  line-height: 1.6;
}

.error-details {
  margin-top: 1rem;
  padding: 1rem;
  background-color: var(--app-surface-ground);
  border-left: 3px solid var(--app-error);
  font-family: monospace;
  font-size: 0.875rem;
  color: var(--app-error);
  text-align: left;
  word-break: break-word;
}

/* Built-bundle reference id — subtle, non-alarming (not the red error style). */
.error-reference {
  margin-top: 1rem;
  font-family: monospace;
  font-size: 0.8125rem;
  color: var(--app-text-secondary);
  word-break: break-all;
}

.retry-button {
  margin-top: 1.5rem;
  padding: 0.75rem 1.5rem;
  background-color: var(--app-primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background-color: var(--app-primary-dark);
}

.retry-button:focus {
  outline: 2px solid var(--app-primary-color);
  outline-offset: 2px;
}
</style>
